import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HcPopComponent } from '@healthcatalyst/cashmere';
import {
    customizableTimeRangeOptions,
    RelativeTimeRange,
    TimeRangeDirection,
    TimeRangeInterval,
    TimeRangeOption,
    TimeRangePreset
} from './relative-time-range.models';

let TimeRangeOptionId = 3;

@Component({
    selector: 'cdp-relative-time-range-selector',
    templateUrl: 'relative-time-range-selector.component.html',
    styleUrls: ['relative-time-range-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RelativeTimeRangeSelectorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class RelativeTimeRangeSelectorComponent implements ControlValueAccessor, OnInit {
    @ViewChild(HcPopComponent) pop: HcPopComponent;
    @Input() public set timeRangePresets(value: TimeRangePreset[]) {
        this._timeRangePresets = value;
        this.resetTimeRangeOptions();
        this.setSelectedRange();
    }
    public get timeRangePresets(): TimeRangePreset[] {
        return this._timeRangePresets;
    }
    private _timeRangePresets: TimeRangePreset[] = [];
    public timeRangeOptions: TimeRangeOption[];
    private _timeRange: RelativeTimeRange; // ControlValueAccessor model
    public get timeRange(): RelativeTimeRange {
        return this._timeRange;
    }
    public set timeRange(newValue: RelativeTimeRange) {
        this._timeRange = { ...newValue };
    }
    public selectedRangeLabel = '';
    public selectedPreset?: TimeRangePreset;
    public timeRangeForm: number;
    public readonly TimeRangeInterval = TimeRangeInterval;
    public readonly TimeRangeDirection = TimeRangeDirection;

    public ngOnInit(): void {
        this.resetTimeRangeOptions();
        this.setSelectedRange();
    }

    // ControlValueAccessor
    public onChange: (value: any) => void = () => undefined;
    public onTouch: () => any = () => undefined;
    public writeValue(model: RelativeTimeRange): void {
        this.timeRange = model;
        this.setSelectedRange();
    }
    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => any): void {
        this.onTouch = fn;
    }

    resetTimeRangeOptions() {
        this.timeRangeOptions = this.timeRangePresets.map(p => ({
            id: TimeRangeOptionId++,
            timeRange: p,
            isCustom: false
        }));
        this.timeRangeOptions.push(...customizableTimeRangeOptions);
    }

    setSelectedRange() {
        if (!this.timeRange) {
            this.timeRange = this.timeRangeOptions[0].timeRange;
        }
        const selectedOption = this.findExactPresetMatch() || this.findCustomOptionMatch() || this.timeRangeOptions[0];
        this.timeRangeForm = selectedOption.id;
        this.onChange(this.timeRange);
        this.setTimeRangeLabel();
    }

    done(pop: HcPopComponent) {
        this.setSelectedRangeIfChanged();
        pop.close();
    }

    setSelectedRangeIfChanged(): void {
        const selectedOption = this.timeRangeOptions.find(f => f.id === this.timeRangeForm);
        if (selectedOption && !this.timeRangesAreEqual(selectedOption.timeRange, this.timeRange)) {
            this.timeRange = selectedOption.timeRange;
            this.setSelectedRange();
        }
    }

    timeRangesAreEqual(a: RelativeTimeRange, b: RelativeTimeRange): boolean {
        return a.interval === b.interval && a.value === b.value && a.direction === b.direction;
    }

    onCustomRadioClick(valInput: HTMLInputElement) {
        setTimeout(() => {
            valInput?.focus();
            valInput?.select();
        }, 50);
    }

    validateInput(valueInput: HTMLInputElement, timeRange: RelativeTimeRange) {
        if (parseInt(valueInput.value) > this.getMaxForInterval(timeRange.interval)) {
            timeRange.value = this.getMaxForInterval(timeRange.interval);
        } else if (parseInt(valueInput.value) < 1) {
            timeRange.value = 1;
        }
    }

    getMaxForInterval(interval: TimeRangeInterval): number {
        switch (interval) {
            case TimeRangeInterval.RelativeHours:
                return 200;
            case TimeRangeInterval.RelativeDays:
                return 30;
            case TimeRangeInterval.RelativeMonths:
                return 12;
            default:
                return 0;
        }
    }

    getStrForInterval(interval: TimeRangeInterval, value = 0): string {
        switch (interval) {
            case TimeRangeInterval.RelativeHours:
                return `hour${value === 1 ? '' : 's'}`;
            case TimeRangeInterval.RelativeDays:
                return `day${value === 1 ? '' : 's'}`;
            case TimeRangeInterval.RelativeMonths:
                return `month${value === 1 ? '' : 's'}`;
            default:
                return '';
        }
    }

    private findExactPresetMatch(): TimeRangeOption | undefined {
        return this.timeRangeOptions.find(
            f =>
                f.timeRange.interval === this.timeRange.interval &&
                f.timeRange.value === this.timeRange.value &&
                f.timeRange.direction === this.timeRange.direction
        );
    }

    private findCustomOptionMatch(): TimeRangeOption | undefined {
        const optionMatch = this.timeRangeOptions.find(
            f =>
                f.isCustom &&
                f.timeRange.interval === this.timeRange.interval &&
                f.timeRange.direction === this.timeRange.direction
        );
        if (optionMatch) {
            optionMatch.timeRange.value = this.timeRange.value;
        }
        return optionMatch;
    }

    private setTimeRangeLabel() {
        const selectedRangePreset = this.timeRangePresets.find(
            f =>
                f.interval === this.timeRange.interval &&
                f.value === this.timeRange.value &&
                f.direction === this.timeRange.direction
        );
        this.selectedRangeLabel = selectedRangePreset ? selectedRangePreset.name : this.buildCustomRangeLabel();
    }

    private buildCustomRangeLabel(): string {
        const dirStr = this.timeRange.direction === TimeRangeDirection.BeforeNow ? 'Last' : 'Next';
        const intervalStr = this.getStrForInterval(this.timeRange.interval, this.timeRange.value);
        return `${dirStr} ${this.timeRange.value} ${intervalStr}`;
    }
}
